import type { FloraCategory, FloraDefinition } from '@/flora/FloraDefinition';
import { clampUnit, type BrushPoint } from '@/terrain/TerrainBrush';

/**
 * The planting brush: scattering a drift of one species along a dragged stroke.
 *
 * ## Why placing a groundcover one click at a time is the wrong tool
 *
 * A tree is a decision. You choose where the maple goes, and you are still looking at that spot ten
 * minutes later. A groundcover is not a decision about individuals at all — nobody places three
 * hundred vinca, they plant *a bed of vinca*, and the position of any one of them is noise. Handing a
 * designer the specimen tool for that job asks them to make three hundred decisions that do not
 * matter, and the site pays for it twice: once in the time, and again because a hand-clicked mat is
 * laid out on a grid the eye reads instantly as artificial.
 *
 * So the same press-drag-release gesture the ground tools already use (`TerrainBrush`) is offered for
 * planting, and this module is the part that decides *where the plants land*.
 *
 * ## Darts, not a grid
 *
 * Each dab throws a handful of candidate points uniformly into its disc and keeps the ones that are
 * far enough from everything already planted in the stroke — dart throwing, the cheap cousin of
 * Poisson-disc sampling. It is chosen over a jittered grid for one reason: a grid with jitter still
 * has a grid's spatial frequency, and at a bed's worth of plants the rows reappear to the eye no
 * matter how much noise is added to them. Rejection sampling has no preferred direction at all.
 *
 * The rejection radius comes from the plant's own `recommendedSpacingMeters` rather than from a
 * brush setting, so a 45 cm pachysandra and a 1.2 m hosta fill at their own honest densities from
 * the same brush; {@link scatterSpacingMeters} is where the density control leans on that figure
 * instead of replacing it.
 *
 * ## What the spacing test does *not* look at
 *
 * Only plants of the same species. Underplanting a red oak with vinca is ordinary garden design, and
 * a brush that refused to plant within the oak's spacing would make the single most common use of it
 * impossible. Stacking two vinca in the same 30 cm is not design, it is a mistake, and that is the
 * one the test exists to prevent. Everything else a plant can be refused for — a wall, a driveway, a
 * slope it will slide off — belongs to `evaluatePlacement` and is checked there.
 */

export type PlantToolMode = 'spot' | 'paint';

export const PLANT_TOOL_MODES: readonly PlantToolMode[] = ['spot', 'paint'];

/** How the two modes are named wherever the app says which one is in hand. */
const MODE_LABELS: Record<PlantToolMode, string> = {
  spot: 'Place one',
  paint: 'Paint a drift',
};

export function plantModeLabel(mode: PlantToolMode): string {
  return MODE_LABELS[mode];
}

export function otherPlantMode(mode: PlantToolMode): PlantToolMode {
  return mode === 'paint' ? 'spot' : 'paint';
}

/**
 * Plants that are chosen individually, whatever their spacing says.
 *
 * A dwarf conifer can have a 60 cm spacing and still be a specimen: you buy one, you site it, and
 * you look at it. Woody plants are placed; herbaceous ones are laid out. That is the line, and it is
 * drawn on the category rather than on the size because it is a fact about how the plant is *used*.
 */
const SPECIMEN_CATEGORIES: readonly FloraCategory[] = [
  'deciduous-tree',
  'evergreen-tree',
  'deciduous-shrub',
  'evergreen-shrub',
];

/**
 * Spacing at or under which a herbaceous plant is normally planted in drifts rather than singly.
 *
 * 1.2 m is where nursery practice changes its language: below it a plan says "x per m²", above it a
 * plan names the plants. Perennials and grasses either side of that line get the tool that matches.
 */
export const DRIFT_SPACING_METERS = 1.2;

/**
 * The mode the plant tool arms itself in for a given species (spec §3.5).
 *
 * A default rather than a setting because the answer is a property of the plant, and asking the user
 * to pick a tool mode every time they pick a plant is asking them to re-derive something the catalog
 * already knows. Both directions stay one keystroke away — see the `Ctrl` handling in
 * `PlacementTool` — so a default that is wrong for a particular bed costs one tap, not a trip to a
 * settings panel.
 */
export function defaultPlantMode(def: FloraDefinition): PlantToolMode {
  if (def.category === 'groundcover') return 'paint';
  if (SPECIMEN_CATEGORIES.includes(def.category)) return 'spot';
  return def.placement.recommendedSpacingMeters <= DRIFT_SPACING_METERS ? 'paint' : 'spot';
}

/** How wide the brush is, and how tightly it packs. Radius is world metres. */
export interface PlantBrushShape {
  radiusMeters: number;
  /**
   * 0–1, where 0.5 plants at exactly the species' recommended spacing. Below that the drift thins
   * toward a naturalized scatter; above it, toward the nursery's own "plant closer for quick cover".
   */
  density: number;
}

export const MIN_PLANT_BRUSH_RADIUS = 0.25;
export const MAX_PLANT_BRUSH_RADIUS = 20;

export const DEFAULT_PLANT_BRUSH: PlantBrushShape = { radiusMeters: 1.5, density: 0.5 };

export function clampPlantBrushRadius(meters: number): number {
  if (!Number.isFinite(meters)) return DEFAULT_PLANT_BRUSH.radiusMeters;
  return Math.min(MAX_PLANT_BRUSH_RADIUS, Math.max(MIN_PLANT_BRUSH_RADIUS, meters));
}

/**
 * Centre-to-centre spacing the brush will hold, in metres.
 *
 * The density control scales the species' own figure rather than replacing it: at 0.5 you get
 * exactly what the catalog recommends, at 1 you get half of it (planted for cover in one season),
 * and at 0 you get one and a half times it (a naturalized drift left room to spread into). A control
 * that set an absolute spacing would need re-setting for every plant, and would quietly let someone
 * plant hostas at 15 cm.
 */
export function scatterSpacingMeters(def: FloraDefinition, density: number): number {
  const recommended = Math.max(0.05, def.placement.recommendedSpacingMeters);
  return recommended * (1.5 - clampUnit(density, DEFAULT_PLANT_BRUSH.density));
}

/**
 * A brush wide enough to lay a useful patch of this species in one pass, but still aimable.
 *
 * Four plants across, which is the width at which a single stroke reads as a bed rather than as a
 * line — narrower and the user ends up hatching back and forth to fill anything.
 */
export function suggestedBrushRadius(def: FloraDefinition): number {
  return clampPlantBrushRadius(def.placement.recommendedSpacingMeters * 2);
}

/** Dab interval along the stroke, as a fraction of the brush radius. */
export const PLANT_DAB_SPACING = 0.3;

/** Dabs per second a brush held still over one spot pays out. */
export const PLANT_HOLD_DABS_PER_SECOND = 12;

/** Ceiling on one stroke, so a brush left leaning on the mouse cannot fill a site. */
export const MAX_PLANTS_PER_STROKE = 500;

/** Ceiling on the darts thrown by one dab, whatever the arithmetic below asks for. */
export const MAX_DARTS_PER_DAB = 64;

/**
 * How many candidate points one dab throws.
 *
 * Scaled by how many plants would fit in the disc — `(radius / spacing)²` — because a wide brush on
 * a tight groundcover has to fill far more slots than a narrow one on a hosta, and a fixed count
 * would either crawl on the first or waste most of its darts on the second. The 0.6 is empirical:
 * with the dab spacing above, a stroke passes over each patch of ground three or four times, and
 * this is the rate at which a disc reaches its packing limit inside about that many passes.
 */
export function dartsPerDab(radiusMeters: number, spacingMeters: number): number {
  const slots = radiusMeters / Math.max(0.05, spacingMeters);
  return Math.min(MAX_DARTS_PER_DAB, Math.max(1, Math.round(slots * slots * 0.6)));
}

/** A source of numbers in [0, 1). Injected so a scatter can be reproduced in a test. */
export type Random = () => number;

/**
 * `count` candidate points thrown uniformly into the dab's disc.
 *
 * `sqrt` on the radius is the whole trick: sampling the radius linearly piles two-thirds of the
 * points into the inner half of the disc, which shows up as a dense core and a thin rim on every
 * stroke.
 */
export function scatterDarts(
  center: BrushPoint,
  radiusMeters: number,
  count: number,
  random: Random = Math.random,
): BrushPoint[] {
  const darts: BrushPoint[] = [];
  for (let i = 0; i < count; i += 1) {
    const angle = random() * Math.PI * 2;
    const distance = Math.sqrt(random()) * radiusMeters;
    darts.push({
      x: center.x + Math.cos(angle) * distance,
      z: center.z + Math.sin(angle) * distance,
    });
  }
  return darts;
}

/**
 * Everything planted so far, indexed so "is anything already here?" costs a fixed nine buckets
 * rather than a walk over the whole bed.
 *
 * The cell size *is* the spacing, which is what bounds the search: a point closer than `spacing` to
 * an occupant must share its cell or touch one of the eight around it, so nothing further out can
 * ever matter. A stroke that lays four hundred plants therefore costs four hundred constant-time
 * tests instead of eighty thousand distance checks.
 */
export class ScatterField {
  private readonly cells = new Map<string, BrushPoint[]>();
  private placed = 0;

  constructor(private readonly spacingMeters: number) {}

  /** How many points {@link accept} has taken. Seeded points are not counted — they were already there. */
  get count(): number {
    return this.placed;
  }

  /** Record a point that is already planted, without counting it as part of this stroke. */
  seed(x: number, z: number): void {
    this.insert(x, z);
  }

  /** True when the spot is clear — and, when it is, the point is taken. */
  accept(x: number, z: number): boolean {
    if (!this.isClear(x, z)) return false;
    this.insert(x, z);
    this.placed += 1;
    return true;
  }

  /** Whether a point is far enough from everything recorded. Exposed for tests and for previews. */
  isClear(x: number, z: number): boolean {
    const spacing = Math.max(1e-3, this.spacingMeters);
    const col = Math.floor(x / spacing);
    const row = Math.floor(z / spacing);
    const limit = spacing * spacing;
    for (let dc = -1; dc <= 1; dc += 1) {
      for (let dr = -1; dr <= 1; dr += 1) {
        const bucket = this.cells.get(`${col + dc}|${row + dr}`);
        if (!bucket) continue;
        for (const point of bucket) {
          const dx = point.x - x;
          const dz = point.z - z;
          if (dx * dx + dz * dz < limit) return false;
        }
      }
    }
    return true;
  }

  private insert(x: number, z: number): void {
    const spacing = Math.max(1e-3, this.spacingMeters);
    const key = `${Math.floor(x / spacing)}|${Math.floor(z / spacing)}`;
    const bucket = this.cells.get(key);
    if (bucket) bucket.push({ x, z });
    else this.cells.set(key, [{ x, z }]);
  }
}

/**
 * Per-specimen variation for a scattered plant, so a drift does not read as one model stamped out.
 *
 * Maturity varies more than it would for a specimen — a mat of groundcover is genuinely a mix of
 * ages, where a row of clipped box is not — and the rotation is free, since none of these plants has
 * a front.
 */
export interface ScatterVariation {
  maturity: number;
  rotationY: number;
}

/** Spread of maturities within one drift, either side of the brush's own setting. */
export const SCATTER_MATURITY_JITTER = 0.18;

export function scatterVariation(
  baseMaturity: number,
  random: Random = Math.random,
): ScatterVariation {
  const jitter = (random() * 2 - 1) * SCATTER_MATURITY_JITTER;
  return {
    maturity: Math.min(1, Math.max(0.05, baseMaturity + jitter)),
    rotationY: random() * Math.PI * 2,
  };
}
